import { useToast } from '@cartwheel/components'
import { useEffect, useRef } from 'react'

export interface FlashToastProps {
  variant: 'success' | 'error' | 'warning' | 'information'
  description: string
  linkText?: string | null
  linkHref?: string | null
  timeout?: number | null
}

/**
 * FlashToast - Bridge component that displays Rails flash messages as React Toasts
 *
 * This component is rendered via render_react_component in _flash_alerts.html.erb.
 * It uses the useToast hook to show a toast notification on mount, then renders nothing.
 *
 * @example
 * // In Rails view:
 * <%= render_react_component("components/flash-toast", props: {
 *   variant: "success",
 *   description: "Changes saved successfully"
 * }) %>
 */
export default function FlashToast({
  variant,
  description,
  linkText,
  linkHref,
  timeout,
}: FlashToastProps) {
  const toast = useToast()
  const hasShownRef = useRef(false)

  useEffect(() => {
    // Only show toast once - prevents infinite loops from toast object reference changes
    if (hasShownRef.current) return
    hasShownRef.current = true

    const options: {
      description: string
      link?: { text: string; href: string }
      timeout?: number
    } = {
      description,
    }

    // Add link if both text and href are provided
    if (linkText && linkHref) {
      options.link = { text: linkText, href: linkHref }
    }

    // Add timeout if specified (0 means no auto-dismiss)
    if (timeout !== undefined && timeout !== null) {
      options.timeout = timeout
    }

    // Use setTimeout to defer the toast call outside of React's rendering cycle.
    // Base UI's toast manager uses flushSync internally, which cannot be called
    // from inside a lifecycle method (useEffect). Deferring with setTimeout(0)
    // ensures we're outside React's commit phase.
    const timeoutId = setTimeout(() => {
      switch (variant) {
        case 'success':
          toast.success(options)
          break
        case 'error':
          toast.error(options)
          break
        case 'warning':
          toast.warning(options)
          break
        default:
          toast.info(options)
          break
      }
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [description, variant, linkText, linkHref, timeout, toast])

  // This component only triggers the toast on mount, it doesn't render anything
  return null
}
